import React from 'react';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAccountData, useStorefrontData } from '../StorefrontThemeWrapper';
import { StorefrontHeader } from '../common/StorefrontHeader';
import { ExperiencesSection } from './experiencesSection/ExperiencesSection';
import { ReviewsSection } from './reviewsSection/ReviewsSection';
import { FAQsSection } from './faqsSection/FAQsSection';
import { AboutSection } from './aboutSection/AboutSection';
import { CustomInquirySection } from './customInquirySection/CustomInquirySection';
import { SubscriptionsSection } from './subscriptionsSection/SubscriptionsSection';
import { TrialsSection } from './trialsSection/TrialsSection';
import { GiftCardSection } from './giftCardSection/GiftCardSection';
import { StorefrontFooter } from '../common/StorefrontFooter';
import { StorefrontVoucherBalanceBanner } from '../common/StorefrontVoucherBalanceBanner';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageData, PassOrderPageData, StorefrontSection, StorefrontStatus } from '../../api';
import { isPassHiddenFromCustomer } from '../../managePassesPage/utils/passUtils';

export const StorefrontPage = () => {
  const account = useAccountData();
  const storefront = useStorefrontData();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const language = useLanguage();
  const businessDetails = account?.business_details;
  const orderPages = account?.experiences?.[0]?.order_pages;
  const storefrontsOrderPages = storefront?.order_page_ids
    ?.map((id) => orderPages?.find((op) => op.id === id))
    .filter((op): op is OrderPageData => !!op);
  const storefrontTrialOrderPages = storefront?.trial_order_page_ids
    ?.map((id) => orderPages?.find((op) => op.id === id))
    .filter((op): op is OrderPageData => !!op);
  const passOrderPages = account?.pass_order_pages;
  const storefrontPassOrderPages = storefront?.pass_order_page_ids
    ?.map((id) => passOrderPages?.find((pop) => pop.id === id))
    .filter((pop): pop is PassOrderPageData => !!pop && !isPassHiddenFromCustomer(account, pop.id));

  if (!storefront || !account) {
    return null;
  }

  return (
    <Grid
      container
      flexDirection="column"
      sx={{
        maxWidth: 1800,
        margin: '0 auto',
        padding: isSmallScreen ? '0 16px' : '0 80px',
      }}>
      <StorefrontHeader />
      {storefront.status !== StorefrontStatus.ARCHIVED && <StorefrontVoucherBalanceBanner />}
      {storefront.status === StorefrontStatus.ARCHIVED ? (
        <Grid container justifyContent="center" alignItems="center" py={10}>
          <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center' }}>
            {t('storefront.archivedMessage')}
          </Typography>
        </Grid>
      ) : (
        (storefront.displaySections && storefront.displaySections?.length > 0
          ? storefront.displaySections
          : [
              StorefrontSection.ORDER_PAGES,
              StorefrontSection.TRIALS,
              StorefrontSection.PASS_ORDER_PAGES,
              StorefrontSection.REVIEWS,
              StorefrontSection.FAQS,
              StorefrontSection.ABOUT,
            ]
        ).map((section) => {
          switch (section) {
            case StorefrontSection.ORDER_PAGES:
              return (
                <ExperiencesSection
                  key={section}
                  orderPages={storefrontsOrderPages || []}
                  subtitle={storefront.subtitle}
                />
              );
            case StorefrontSection.REVIEWS:
              return <ReviewsSection key={section} reviews={storefront.reviews || []} />;
            case StorefrontSection.FAQS:
              return <FAQsSection key={section} faqs={storefront.faqs || []} />;
            case StorefrontSection.ABOUT:
              return (
                <AboutSection
                  key={section}
                  businessDetails={businessDetails}
                  accountName={account.label?.[language] || ''}
                />
              );
            case StorefrontSection.PASS_ORDER_PAGES:
              return <SubscriptionsSection key={section} passOrderPages={storefrontPassOrderPages || []} />;
            case StorefrontSection.GIFT_CARD:
              return <GiftCardSection key={section} />;
            case StorefrontSection.TRIALS:
              return <TrialsSection key={section} orderPages={storefrontTrialOrderPages || []} />;
            case StorefrontSection.CUSTOM_INQUIRY:
              return <CustomInquirySection key={section} customInquiry={storefront.custom_inquiry} />;
            default:
              return null;
          }
        })
      )}
      <StorefrontFooter businessDetails={businessDetails} />
    </Grid>
  );
};
